/* START_MODULE_CONTRACT
 * PURPOSE: Мастер регистрации из 3 шагов в фирменном split-layout auth-страниц.
 * SCOPE: React-компонент, шаги email → код → пароль, индикатор прогресса.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-AUTH
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * RegisterPage - страница регистрации TeamPlanner.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-08-10 - UC-022: redirect when enableRegistration=false.
 * LAST_CHANGE: 2026-07-13 - Редизайн в стиле login: split-layout, кастомный stepper.
 * END_CHANGE_SUMMARY
 */

import { useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import AuthPageHero from '../AuthPageHero';
import RegisterStepEmail from './RegisterStepEmail';
import RegisterStepCode from './RegisterStepCode';
import RegisterStepPassword from './RegisterStepPassword';
import { useFeatureToggle } from '../../context/FeatureToggleContext';
import { FLAG_ENABLE_REGISTRATION } from '../../features/featureToggle/featureToggleStorage';
import '../AuthPage.css';

const STEPS = [
  { id: 'email', label: 'Email' },
  { id: 'code', label: 'Подтверждение' },
  { id: 'password', label: 'Пароль' },
];

const STEP_HINTS = [
  'Укажите email — отправим код подтверждения.',
  'Введите код из письма.',
  'Придумайте пароль для входа.',
];

/* START_METHOD_RegisterPage */
/* START_CONTRACT: RegisterPage
 * PURPOSE: Мастер регистрации из 3 шагов (email → код → пароль) с общим состоянием email.
 * INPUTS: нет.
 * OUTPUTS: { JSX.Element - Stepper + активный шаг }.
 * SIDE_EFFECTS: нет (делегирует шагам).
 * LINKS: M-FE-AUTH
 * END_CONTRACT: RegisterPage
 */
export default function RegisterPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const { isEnabled, loading: flagsLoading } = useFeatureToggle();

  if (!flagsLoading && !isEnabled(FLAG_ENABLE_REGISTRATION)) {
    return <Navigate to="/login" replace />;
  }

  /* START_METHOD_handleNext */
  /* START_CONTRACT: handleNext
   * PURPOSE: Перейти к следующему шагу мастера регистрации.
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setActiveStep.
   * LINKS: M-FE-AUTH
   * END_CONTRACT: handleNext
   */
  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };
  /* END_METHOD_handleNext */

  return (
    <div className="auth-page">
      <AuthPageHero />

      <section className="auth-page__panel" aria-labelledby="register-form-title">
        <div className="auth-page__form-wrap">
          <h2 id="register-form-title" className="auth-page__form-title">
            Регистрация
          </h2>
          <p className="auth-page__form-sub">{STEP_HINTS[activeStep]}</p>

          <ol className="auth-page__steps" aria-label="Шаги регистрации">
            {STEPS.map((step, index) => {
              let stepClass = 'auth-page__step';
              if (index < activeStep) {
                stepClass += ' auth-page__step--done';
              } else if (index === activeStep) {
                stepClass += ' auth-page__step--active';
              }

              return (
                <li key={step.id} className={stepClass} aria-current={index === activeStep ? 'step' : undefined}>
                  {step.label}
                </li>
              );
            })}
          </ol>

          {activeStep === 0 && (
            <RegisterStepEmail
              email={email}
              setEmail={setEmail}
              onNext={handleNext}
            />
          )}
          {activeStep === 1 && (
            <RegisterStepCode email={email} onNext={handleNext} />
          )}
          {activeStep === 2 && <RegisterStepPassword email={email} />}

          <p className="auth-page__footer">
            Уже есть аккаунт?{' '}
            <RouterLink className="auth-page__link" to="/login">
              Войти
            </RouterLink>
          </p>
        </div>
      </section>
    </div>
  );
}
/* END_METHOD_RegisterPage */
